/**
 * SHARING — "one click, straight to the point".
 *
 * Every shared link is the CANONICAL, language-prefixed URL of the thing being
 * shared (e.g. https://giggone.com/en/jobs/<slug>). No tracking redirect, no
 * intermediate page, and no bare /jobs/<slug> that would bounce through the
 * language redirect first — the recipient's phone opens the content directly.
 */
export const SITE_ORIGIN = 'https://giggone.com';

/** Absolute canonical URL for an already-localised path like `/en/jobs/abc`. */
export function canonicalUrl(localisedPath: string): string {
  const origin =
    typeof window !== 'undefined' && /giggone\.com$/i.test(window.location.hostname)
      ? window.location.origin
      : SITE_ORIGIN;
  const p = localisedPath.startsWith('/') ? localisedPath : `/${localisedPath}`;
  return `${origin}${p}`;
}

export type ShareOutcome = 'shared' | 'copied' | 'cancelled' | 'failed';

/** Native share sheet on phones; clipboard copy everywhere else. */
export async function shareLink(opts: { url: string; title: string; text?: string }): Promise<ShareOutcome> {
  const nav: any = typeof navigator !== 'undefined' ? navigator : null;
  if (nav?.share) {
    try {
      await nav.share({ title: opts.title, text: opts.text || opts.title, url: opts.url });
      return 'shared';
    } catch (e: any) {
      if (e?.name === 'AbortError') return 'cancelled';
      // fall through to copy
    }
  }
  return copyLink(opts.url);
}

export async function copyLink(url: string): Promise<ShareOutcome> {
  try {
    await navigator.clipboard.writeText(url);
    return 'copied';
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok ? 'copied' : 'failed';
    } catch {
      return 'failed';
    }
  }
}

/** Direct app links for the share menu (open the target app with the URL pre-filled). */
export const shareTargets = (url: string, text: string) => ({
  whatsapp: `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`,
  sms: `sms:?&body=${encodeURIComponent(`${text} ${url}`)}`,
  email: `mailto:?subject=${encodeURIComponent(text)}&body=${encodeURIComponent(`${text}\n\n${url}`)}`,
  facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
});
